/**
 * IST (Asia/Kolkata) Timezone Patch
 *
 * The entire SY-Store business (stores, HQ, warehouses, customers) operates in
 * India. The backend stores timestamps in UTC (ISO strings) which is correct,
 * but the frontend displays those timestamps using whatever timezone the user's
 * browser reports -- often UTC on servers, or wrong timezones for users logging
 * in from abroad.
 *
 * This module overrides the three Date formatters that are used across ~90
 * files to default to the "Asia/Kolkata" timezone when the caller does not
 * specify one explicitly. This makes every date/time rendered anywhere in the
 * app -- cash register reports, bills, GRNs, STOs, dashboards, ledgers --
 * display in IST (dd/MM/yyyy hh:mm AM/PM) without having to patch each
 * individual call site.
 *
 * Callers that pass an explicit `timeZone` option continue to behave as
 * before -- their intent is respected.
 *
 * Import side-effect only -- include this exactly once from index.js, before
 * any component renders.
 */

const IST_TZ = 'Asia/Kolkata';
const DEFAULT_LOCALE = 'en-IN';

const origToLocaleString = Date.prototype.toLocaleString;
const origToLocaleDateString = Date.prototype.toLocaleDateString;
const origToLocaleTimeString = Date.prototype.toLocaleTimeString;

const withIST = (options) => {
  if (!options || typeof options !== 'object') return { timeZone: IST_TZ };
  if (options.timeZone) return options; // respect explicit caller intent
  return { ...options, timeZone: IST_TZ };
};

const withLocale = (locales) => locales || DEFAULT_LOCALE;

// eslint-disable-next-line no-extend-native
Date.prototype.toLocaleString = function (locales, options) {
  return origToLocaleString.call(this, withLocale(locales), withIST(options));
};

// eslint-disable-next-line no-extend-native
Date.prototype.toLocaleDateString = function (locales, options) {
  return origToLocaleDateString.call(this, withLocale(locales), withIST(options));
};

// eslint-disable-next-line no-extend-native
Date.prototype.toLocaleTimeString = function (locales, options) {
  return origToLocaleTimeString.call(this, withLocale(locales), withIST(options));
};

// Also patch Intl.DateTimeFormat so direct `new Intl.DateTimeFormat(...)` calls
// without a timeZone also default to IST. Our shared formatDate utility already
// uses this correctly, but third-party components or future code may not.
const OrigDTF = Intl.DateTimeFormat;
function PatchedDTF(locales, options) {
  const opts = (options && typeof options === 'object' && options.timeZone)
    ? options
    : { ...(options || {}), timeZone: IST_TZ };
  return new OrigDTF(withLocale(locales), opts);
}
PatchedDTF.supportedLocalesOf = OrigDTF.supportedLocalesOf.bind(OrigDTF);
Intl.DateTimeFormat = PatchedDTF;
